import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  advanceQuestionFlowAction,
  analyzeSuggestionsAction,
  fetchDefectCodeAdviceAction,
  fetchSuggestedAmendCommentAction,
  isQuestionUnanswered,
  pollSummaryAction,
} from '../actions/defectAssistant'
import {
  DefectAssistantImage,
  DefectAssistantSuggestion,
  FetchedText,
  FinalizationStates,
  FlowStep,
  FutureQuestion,
  OptionValue,
  QnA,
  ReportDefectBody,
  SuggestionRound,
  SummaryState,
} from '../actions/defectAssistant/types'
import type { RootState } from '../index'

/** Consecutive failed polls before the stream is written off and the blocking re-run takes over. */
const MAX_FAILED_POLLS = 3

const idleSummary: SummaryState = {
  finalizationId: null,
  text: '',
  status: 'idle',
  fallbackAttempted: false,
  failedPolls: 0,
}

export interface CreateDefectAssistantState {
  flow: FlowStep
  /** The questions turn started last. Only that turn's abort may stop the loading. */
  questionRequestId: string | null
  suggestionAttempts: number
  images: DefectAssistantImage[]
  baseComment: string
  discardedDefectCodes: string[]
  rounds: SuggestionRound[]
  /** Round containing the selected defect code, or `null` when none is selected. */
  pickedRoundIdx: number | null
  /** Advice keyed by generation and code prefix so late responses update their source round. */
  defectCodeAdviceByGeneration: Record<string, Record<string, FetchedText>>
  body: ReportDefectBody
  questionAnswers: QnA[]
  /** Steps the assistant says it still plans to ask; rendered as placeholders after the real ones. */
  futureQuestions: FutureQuestion[]
  summary: SummaryState
  /** Keyed by suggestedCommentId, so a late reply can't land on a newer match. */
  suggestedAmendCommentById: Record<string, FetchedText>
}

type InitializeCreateDefectAssistantPayload = {
  comment: string
  images: DefectAssistantImage[]
  vehicleType: string
  installationGroup: string
}

const initialState: CreateDefectAssistantState = {
  flow: { step: 'idle' },
  questionRequestId: null,
  suggestionAttempts: 1,
  images: [],
  baseComment: '',
  discardedDefectCodes: [],
  rounds: [],
  pickedRoundIdx: null,
  defectCodeAdviceByGeneration: {},
  body: {
    userComment: '',
    aiComment: '',
    defectCode: '',
    defectType: '',
    restrictionCode: '',
    installationGroup: '',
    vehiclePosition: null,
    vehicleSeries: '',
  },
  questionAnswers: [],
  futureQuestions: [],
  summary: idleSummary,
  suggestedAmendCommentById: {},
}

const setDefectCodeAdvice = (
  state: CreateDefectAssistantState,
  generationId: string,
  prefix: string,
  entry: FetchedText
): void => {
  if (!state.defectCodeAdviceByGeneration[generationId]) {
    state.defectCodeAdviceByGeneration[generationId] = {}
  }
  state.defectCodeAdviceByGeneration[generationId][prefix] = entry
}

export const createDefectAssistantSlice = createSlice({
  name: 'createDefectAssistant',
  initialState,
  reducers: {
    initializeCreateDefectAssistant: (
      _state,
      { payload }: PayloadAction<InitializeCreateDefectAssistantPayload>
    ) => ({
      ...initialState,
      images: payload.images,
      baseComment: payload.comment.trim(),
      body: {
        userComment: payload.comment.trim(),
        aiComment: '',
        defectCode: '',
        defectType: '',
        restrictionCode: '',
        vehiclePosition: null,
        installationGroup: payload.installationGroup,
        vehicleSeries: payload.vehicleType,
      },
    }),

    clearCreateDefectAssistantErrors: (state) => {
      if (state.flow.step === 'failed') {
        state.flow = { step: 'choosing' }
      }
    },

    selectSuggestion: (
      state,
      { payload }: PayloadAction<{ suggestion: DefectAssistantSuggestion; roundIdx: number }>
    ) => {
      state.rounds = state.rounds.slice(0, payload.roundIdx + 1)
      state.pickedRoundIdx = payload.roundIdx
      state.body.defectCode = payload.suggestion.defectCode
      state.body.defectType = ''
      state.body.itemId = undefined
      state.body.aiComment = ''
      state.body.vehiclePosition = null
      state.questionAnswers = []
      // futureQuestions stays: it is the plan /comment projected for exactly this flow, and it gives
      // the page its shape while the first question is still in flight.
      state.summary = idleSummary
      state.flow = { step: 'questioning', loading: false }
    },

    submitAdditionalDetails: (state, { payload }: PayloadAction<string>) => {
      const trimmedDetails = payload.trim()
      const lastRound = state.rounds[state.rounds.length - 1]
      if (lastRound) {
        lastRound.detail = trimmedDetails
      }
      state.flow = { step: 'choosing' }

      if (trimmedDetails.length === 0) {
        return
      }
      state.body.userComment =
        state.body.userComment.length > 0
          ? `${state.body.userComment}\n\n${trimmedDetails}`
          : trimmedDetails
    },

    // Records the answer only; the assistant derives the body fields from it on the next turn.
    applyQuestionAnswer: (state, { payload }: PayloadAction<OptionValue[]>) => {
      const targetIdx = state.questionAnswers.findIndex(isQuestionUnanswered)
      if (targetIdx === -1) {
        return
      }

      state.questionAnswers = state.questionAnswers.map((qna, idx) =>
        idx === targetIdx ? { ...qna, answers: payload } : qna
      )
    },

    excludeSuggestions: (state, { payload: roundIdx }: PayloadAction<number>) => {
      const round = state.rounds[roundIdx]
      if (!round) {
        return
      }
      const existing = new Set(state.discardedDefectCodes)
      round.suggestions.forEach((s) => {
        existing.add(s.defectCode)
      })
      state.discardedDefectCodes = Array.from(existing)
      round.excluded = true
      state.suggestionAttempts = state.suggestionAttempts + 1
      if (roundIdx === state.rounds.length - 1 && round.detail !== null) {
        round.detail = null
        state.body.userComment = [
          state.baseComment,
          ...state.rounds.slice(0, roundIdx).map((r) => r.detail ?? ''),
        ]
          .filter((part) => part.length > 0)
          .join('\n\n')
      }
      state.flow = { step: 'detailing' }
      state.pickedRoundIdx = null
      state.body.defectCode = ''
      state.body.defectType = ''
      state.body.itemId = undefined
      state.body.aiComment = ''
      state.body.vehiclePosition = null
      state.questionAnswers = []
      state.futureQuestions = []
      state.summary = idleSummary
    },

    rewindToRound: (state, { payload: roundIdx }: PayloadAction<number>) => {
      const round = state.rounds[roundIdx]
      if (!round) {
        return
      }
      const existing = new Set(state.discardedDefectCodes)
      round.suggestions.forEach((s) => {
        existing.add(s.defectCode)
      })
      state.discardedDefectCodes = Array.from(existing)
      state.suggestionAttempts = state.suggestionAttempts + 1
      state.rounds = state.rounds.slice(0, roundIdx + 1)
      round.excluded = true
      round.detail = null
      state.body.userComment = [
        state.baseComment,
        ...state.rounds.slice(0, roundIdx).map((r) => r.detail ?? ''),
      ]
        .filter((part) => part.length > 0)
        .join('\n\n')
      state.flow = { step: 'detailing' }
      state.pickedRoundIdx = null
      state.body.defectCode = ''
      state.body.defectType = ''
      state.body.itemId = undefined
      state.body.aiComment = ''
      state.body.vehiclePosition = null
      state.questionAnswers = []
      state.futureQuestions = []
      state.summary = idleSummary
    },

    resetAnswersFromIndex: (state, { payload }: PayloadAction<number>) => {
      // Sent back unanswered; the assistant takes back any body fields those answers had set.
      state.questionAnswers = state.questionAnswers
        .slice(0, payload + 1)
        .map((qna, idx) => (idx >= payload ? { ...qna, answers: null } : qna))

      state.body.aiComment = ''
      // The description was written against the answers being discarded; the next finalization
      // starts a fresh generation with a new handle.
      state.summary = idleSummary
    },

    /** Marks the streamed description as beyond saving, so the blocking re-run can take over. */
    failSummaryStream: (state) => {
      if (state.summary.status === 'streaming') state.summary.status = 'failed'
    },

    /** Records that the blocking re-run is under way, so it is attempted exactly once. */
    beginSummaryFallback: (state) => {
      state.summary.fallbackAttempted = true
    },

    resetCreateDefectAssistant: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(analyzeSuggestionsAction.pending, (state) => {
      state.flow = { step: 'analyzing' }
    })

    builder.addCase(analyzeSuggestionsAction.fulfilled, (state, { payload }) => {
      if (state.flow.step === 'questioning') return
      state.rounds.push({
        generationId: payload.generationId,
        suggestions: payload.suggestions,
        metaOptions: payload.metaOptions,
        detail: null,
        excluded: false,
      })
      state.futureQuestions = payload.futureQuestions
      state.flow = { step: 'choosing' }
    })

    builder.addCase(fetchDefectCodeAdviceAction.pending, (state, action) => {
      setDefectCodeAdvice(state, action.meta.arg.generationId, action.meta.arg.prefix, {
        status: 'loading',
        text: '',
      })
    })

    builder.addCase(fetchDefectCodeAdviceAction.fulfilled, (state, action) => {
      const { prefix, defectCodeAdvice, ready } = action.payload
      setDefectCodeAdvice(
        state,
        action.meta.arg.generationId,
        prefix,
        ready && defectCodeAdvice
          ? { status: 'ready', text: defectCodeAdvice }
          : { status: 'failed', text: '' }
      )
    })

    builder.addCase(fetchDefectCodeAdviceAction.rejected, (state, action) => {
      setDefectCodeAdvice(state, action.meta.arg.generationId, action.meta.arg.prefix, {
        status: 'failed',
        text: '',
      })
    })

    builder.addCase(analyzeSuggestionsAction.rejected, (state) => {
      state.flow = { step: 'failed', source: 'analyze' }
    })

    builder.addCase(advanceQuestionFlowAction.pending, (state, action) => {
      state.flow = { step: 'questioning', loading: true }
      state.questionRequestId = action.meta.requestId
    })

    builder.addCase(advanceQuestionFlowAction.fulfilled, (state, { payload, meta }) => {
      state.flow = { step: 'questioning', loading: false }
      state.body = {
        ...payload.body,
        installationGroup: payload.body.installationGroup ?? state.body.installationGroup,
        vehicleSeries: payload.body.vehicleSeries ?? state.body.vehicleSeries,
        itemId:
          payload.body.itemId === undefined ? state.body.itemId : payload.body.itemId ?? undefined,
      }
      state.questionAnswers = payload.questionAnswers
      state.futureQuestions = payload.futureQuestions ?? []

      if (payload.finalizationId) {
        // Streamed: body.aiComment is empty on purpose and the text is polled with this handle. A new
        // handle starts a new generation and resets the fallback — except on the blocking re-run
        // itself, which keeps it spent so a backend that streams anyway cannot loop.
        state.summary = {
          finalizationId: payload.finalizationId,
          text: '',
          status: 'streaming',
          fallbackAttempted: meta.arg?.blockingSummary ?? false,
          failedPolls: 0,
        }
      } else if (payload.body.aiComment) {
        // Written inline — either the blocking fallback, or a backend that does not stream at all.
        state.summary = {
          ...state.summary,
          finalizationId: null,
          text: payload.body.aiComment,
          status: 'done',
        }
      } else {
        state.summary = { ...state.summary, finalizationId: null, text: '', status: 'idle' }
      }
    })

    builder.addCase(advanceQuestionFlowAction.rejected, (state, action) => {
      if (action.meta.aborted) {
        const replaced = action.meta.requestId !== state.questionRequestId
        if (!replaced && state.flow.step === 'questioning') state.flow.loading = false
        return
      }
      // The blocking re-run asks only for the description, so losing it costs the description, not
      // the answers: settle without one rather than show the flow-failed screen, which starts over.
      if (action.meta.arg?.blockingSummary) {
        state.flow = { step: 'questioning', loading: false }
        return
      }
      state.flow = { step: 'failed', source: 'questions' }
    })

    builder.addCase(pollSummaryAction.fulfilled, (state, action) => {
      // A poll that outlived its generation (question re-opened, flow restarted) is dropped.
      if (action.meta.arg.finalizationId !== state.summary.finalizationId) return

      const { summary, state: streamState } = action.payload
      state.summary.failedPolls = 0
      if (summary) state.summary.text = summary
      if (streamState === FinalizationStates.DONE) state.summary.status = 'done'
      else if (streamState !== FinalizationStates.STREAMING) state.summary.status = 'failed'
    })

    builder.addCase(pollSummaryAction.rejected, (state, action) => {
      if (action.meta.arg.finalizationId !== state.summary.finalizationId) return
      // Ride out the odd failed poll — the next snapshot carries the full text anyway — but stop
      // once it is clear the endpoint isn't answering at all, rather than waiting out the deadline.
      state.summary.failedPolls += 1
      if (state.summary.failedPolls >= MAX_FAILED_POLLS) state.summary.status = 'failed'
    })

    builder.addCase(fetchSuggestedAmendCommentAction.pending, (state, action) => {
      state.suggestedAmendCommentById[action.meta.arg.suggestedCommentId] = {
        status: 'loading',
        text: '',
      }
    })

    builder.addCase(fetchSuggestedAmendCommentAction.fulfilled, (state, action) => {
      const { suggestedComment, ready } = action.payload
      state.suggestedAmendCommentById[action.meta.arg.suggestedCommentId] =
        ready && suggestedComment
          ? { status: 'ready', text: suggestedComment }
          : { status: 'failed', text: '' }
    })

    builder.addCase(fetchSuggestedAmendCommentAction.rejected, (state, action) => {
      state.suggestedAmendCommentById[action.meta.arg.suggestedCommentId] = {
        status: 'failed',
        text: '',
      }
    })
  },
})

export const {
  initializeCreateDefectAssistant,
  clearCreateDefectAssistantErrors,
  selectSuggestion,
  submitAdditionalDetails,
  applyQuestionAnswer,
  excludeSuggestions,
  rewindToRound,
  resetAnswersFromIndex,
  failSummaryStream,
  beginSummaryFallback,
  resetCreateDefectAssistant,
} = createDefectAssistantSlice.actions

export const selectCreateDefectAssistantState = (state: RootState): CreateDefectAssistantState =>
  state.createDefectAssistant

/**
 * The steps the assistant still intends to ask. Matched against the questions already returned
 * rather than trusting the plan to empty itself, so a plan that keeps listing a question it has
 * since asked cannot wedge the flow open forever.
 */
export const selectPendingSteps = createSelector(
  (state: RootState) => state.createDefectAssistant.futureQuestions,
  (state: RootState) => state.createDefectAssistant.questionAnswers,
  (futureQuestions, questionAnswers): FutureQuestion[] =>
    futureQuestions.filter((fq) => !questionAnswers.some((q) => q.questionType === fq.type))
)

const selectAssistantIsComplete = (state: RootState): boolean => {
  const qnas = state.createDefectAssistant.questionAnswers
  // Every question asked has been answered and the assistant has nothing else planned.
  return (
    qnas.length > 0 &&
    qnas.every((qna) => !isQuestionUnanswered(qna)) &&
    selectPendingSteps(state).length === 0
  )
}

/**
 * Whether the assistant has nothing left to produce. The description is written after the last
 * answer and streams in, so "every question answered" on its own is too early to send the worker to
 * the form — they would arrive before their description did.
 */
export const selectAssistantIsSettled = (state: RootState): boolean =>
  selectAssistantIsComplete(state) && state.createDefectAssistant.summary.status !== 'streaming'

export default createDefectAssistantSlice.reducer
